<template>
    <div class="bg-grey-lightest">
        <section id="heading" class="pt-12 pb-24">
            <!-- Header Nav -->
            <header-nav ref="headerNav"></header-nav>
        </section>
        <section class="py-12">
            <div class="max-w-sm mx-auto px-6">
                <patient-loading v-if="! timeslot"></patient-loading>
                <div class="card" v-else>
                    <div v-if="step == 'timeslotDetails'">
                        <router-link :to="{ path: '/' }" class="text-grey hover:text-black text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-close"></i></span>
                        </router-link>
                        <div class="p-6">
                            <div class="mb-4">
                                <div class="text-blue text-sm font-semibold mb-1">{{ $t('When_') }}</div>
                                <div class="text-black font-semibold text-lg">{{ timeslot.date | momentFormat('YYYY-MM-DD', $t('date_selector_format')) }} {{ $t('at') }} {{ timeslot.time | momentFormatTime($t('time_format')) }}</div>
                            </div>
                            <div class="mb-4 flex justify-between">
                                <div>
                                    <div class="text-blue text-sm font-semibold mb-1">{{ $t('Who_will_treat_me_') }}</div>
                                    <div class="text-grey-darkest">{{ timeslot.doctorFirstName + ' ' + timeslot.doctorLastName }}</div>
                                    <div class="text-sm text-grey-darker">{{ $t('type_' + timeslot.type) }}</div>
                                    <div class="text-green font-semibold text-sm mt-1" v-if="timeslot.isDoctorRegulated && (timeslot.type !== 'OSTEOPATH' && timeslot.type !== 'PSYCHOLOGIST')"><i class="mdi mdi-currency-eur"></i> {{ $t('Contracted_doctor') }}</div>
                                    <div class="text-red font-semibold text-sm mt-1" v-if="!timeslot.isDoctorRegulated && (timeslot.type !== 'OSTEOPATH' && timeslot.type !== 'PSYCHOLOGIST')"><i class="mdi mdi-currency-eur"></i> {{ $t('Contracted_doctor_negative') }}</div>
                                    <div v-if="timeslot.isDoctorMemberOfOsteopathie" class="tooltip text-green font-semibold text-sm mt-1 mb-2">
                                        <i class="mdi mdi-check-circle"></i>
                                        {{ $t('Member_of_Osteopathie') }}
                                        <span class="tooltip-text text-sm">{{$t('Oste_tooltip')}}</span>
                                    </div>
                                    <!-- OFFICE SETTINGS -->
                                    <OfficeSettings
                                        v-if="timeslot.officeSettings"
                                        :accessible="timeslot.officeSettings.accessible"
                                        :card="timeslot.officeSettings.card"
                                        :cash="timeslot.officeSettings.cash"
                                    />
                                    <!-- OFFICE SETTINGS -->
                                </div>
                                <avatar :avatar="timeslot.doctorAvatar" :name="timeslot.doctorFirstName + ' ' + timeslot.doctorLastName" class="ml-2 w-10 h-10"></avatar>
                            </div>
                            <div class="">
                                <div class="text-blue text-sm font-semibold mb-1">{{ $t('Where_have_I_to_go_') }}</div>
                                <div class="text-grey-darkest">
                                    {{ timeslot.officeStreetNumber }} {{ timeslot.officeStreetName }}<br />
                                    {{ timeslot.officeZipCode }} {{ timeslot.officeLocality }}
                                </div>
                            </div>
                        </div>
                        <gmap-map
                            :center="{lat: timeslot.officeLocation._lat, lng: timeslot.officeLocation._long }"
                            :zoom="13"
                            :options="{disableDefaultUI: true}"
                            style="height: 250px;"
                            class="w-full">
                                <gmap-marker
                                    :position="{lat: timeslot.officeLocation._lat, lng: timeslot.officeLocation._long }"></gmap-marker>
                            </gmap-map>
                        <div class="px-6 py-4" v-if="! isBooked">
                            <button v-if="userId" @click="changeStep('patientForm')" class="bg-blue hover:bg-blue-dark rounded text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3">{{ $t('Book_this_appointment') }}</button>
                            <button v-else @click="showLogin" class="bg-blue-light hover:bg-blue rounded text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3">{{ $t('Sign_in_to_book_this_appointment') }}</button>
                        </div>
                        <div class="px-6 py-4 bg-teal text-center rounded-b" v-else>
                            <span class="text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3">{{ $t('Appointment_confirmed') }}</span>
                        </div>
                    </div>
                    <form @submit.prevent="submitPatientForm" v-if="step == 'patientForm'">
                        <button type="button" @click="changeStep('timeslotDetails')" class="text-grey hover:text-black text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-arrow-left"></i></span>
                        </button>
                        <div class="p-6 pt-12">
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Firstname_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    name="first_name"
                                    v-model="patientForm.firstName"
                                    v-validate="'required'"
                                    :class="{'border-red': errors.has('first_name') }"
                                    type="text">
                                <span v-show="errors.has('first_name')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_first_name') }}</span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Lastname_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    name="last_name"
                                    v-model="patientForm.lastName"
                                    v-validate="'required'"
                                    :class="{'border-red': errors.has('last_name') }"
                                    type="text">
                                <span v-show="errors.has('last_name')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_last_name') }}</span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Age_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    name="patientForm.age"
                                    v-model="patientForm.age"
                                    v-validate="'required|numeric'"
                                    :class="{'border-red': errors.has('patientForm.age') }"
                                    type="number" step="1" min="0">
                                <span v-show="errors.has('patientForm.age')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_age') }}</span>
                            </div>
                            <div class="mb-4" v-if="!patient || patient.email === null">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Email_address_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    name="email"
                                    v-model="patientForm.email"
                                    v-validate="'required|email'"
                                    :class="{'border-red': errors.has('email') }"
                                    type="email">
                                <span v-show="errors.has('email')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_email_address') }}</span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Phone_number_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    @blur="transformPhone"
                                    name="phone"
                                    v-model="patientForm.phone"
                                    v-validate="'required|phone:BE'"
                                    :class="{'border-red': errors.has('phone') }"
                                    type="phone">
                                <span v-show="errors.has('phone')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_phone_number') }}</span>
                            </div>
                            <button type="submit" class="bg-blue hover:bg-blue-dark rounded text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3">{{ $t('Next') }}</button>
                        </div>
                    </form>
                    <form @submit.prevent="submitPatientSymptoms" v-if="step == 'patientSymptoms'">
                        <button type="button" @click="changeStep('patientForm')" class="text-grey hover:text-black text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-arrow-left"></i></span>
                        </button>
                        <div class="p-6 pt-12" v-if="timeslot.type === 'DENTIST'">
                            <div class="mb-4" v-for="qIndex in [0, 1, 2, 3, 4]">
                                <div class="text-blue text-sm font-semibold mb-2" v-html="questions[qIndex].label"></div>
                                <div class="flex flex-wrap -mx-4">
                                    <label v-for="(answer, index) in questions[qIndex].choices" :key="index" class="block group mb-2 px-4" :class="[2,3,4].indexOf(qIndex) >= 0 ? 'w-1/2' : 'w-full'">
                                        <input
                                            type="radio"
                                            class="mr-2"
                                            :name="'symptomsForm.' + qIndex"
                                            v-model="questions[qIndex].answer"
                                            v-validate="'required'"
                                            :value="answer">
                                        <span v-text="answer" class="text-sm" :class="questions[qIndex].answer == answer ? 'text-black' : 'text-grey-dark group-hover:text-grey-darkest'"></span>
                                    </label>
                                </div>
                                <span v-show="errors.has('symptomsForm.' + qIndex)" class="text-xs text-red font-semibold">{{ $t('Please_specify') }}</span>
                            </div>
                            <div class="mb-4" v-for="qIndex in [5,6]" v-if="questions[4].answer == $t('SYMPTOMS_ANSWER_5_1')">
                                <div class="text-blue text-sm font-semibold mb-2" v-html="questions[qIndex].label"></div>
                                <div class="flex flex-wrap -mx-4">
                                    <label v-for="(answer, index) in questions[qIndex].choices" :key="index" class="block group mb-2 px-4" :class="[2,3,4].indexOf(qIndex) >= 0 ? 'w-1/2' : 'w-full'">
                                        <input
                                            type="radio"
                                            class="mr-2"
                                            :name="'symptomsForm.' + qIndex"
                                            v-model="questions[qIndex].answer"
                                            v-validate="'required'"
                                            :value="answer">
                                        <span v-text="answer" class="text-sm" :class="questions[qIndex].answer == answer ? 'text-black' : 'text-grey-dark group-hover:text-grey-darkest'"></span>
                                    </label>
                                </div>
                                <span v-show="errors.has('symptomsForm.' + qIndex)" class="text-xs text-red font-semibold">{{ $t('Please_specify') }}</span>
                            </div>
                            <button type="submit" class="bg-blue hover:bg-blue-dark rounded text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3">{{ $t('Next') }}</button>
                        </div>
                        <div class="p-6 pt-12" v-else>
                            <div class="mb-4" v-for="(question, qIndex) in questions">
                                <div class="text-blue text-sm font-semibold mb-2" v-html="questions[qIndex].label"></div>
                                <div class="flex flex-wrap">
                                    <textarea
                                        class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                        :name="'symptomsForm.' + qIndex"
                                        v-model="questions[qIndex].answer"
                                        v-validate="'required'"
                                        rows="5"></textarea>
                                </div>
                                <span v-show="errors.has('symptomsForm.' + qIndex)" class="text-xs text-red font-semibold">{{ $t('Please_specify') }}</span>
                            </div>
                            <button type="submit" class="bg-blue hover:bg-blue-dark rounded text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3">{{ $t('Next') }}</button>
                        </div>
                    </form>
                    <div v-if="step == 'submissionSummary'">
                        <button @click="changeStep('patientSymptoms')" class="text-grey hover:text-black text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-arrow-left"></i></span>
                        </button>
                        <div class="px-6 py-4">
                            <h4 class="text-xl uppercase tracking-wide text-grey-darkest">{{ $t('Summary') }}</h4>
                        </div>
                        <div class="p-6 pt-4">
                            <div class="mb-4">
                                 <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Name') }}</label>
                                <span class="block w-full text-grey-darker" v-text="patientForm.firstName + ' ' + patientForm.lastName"></span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Age') }}</label>
                                <span class="block w-full text-grey-darker" v-text="patientForm.age"></span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Email_address') }}</label>
                                <span class="block w-full text-grey-darker" v-text="patientForm.email"></span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Phone_number') }}</label>
                                <span class="block w-full text-grey-darker" v-text="patientForm.phone"></span>
                            </div>
                            <div v-if="timeslot.type === 'DENTIST'">
                                <div class="mb-4" v-for="qIndex in [0, 1, 2, 3, 4]">
                                    <div class="text-blue text-sm font-semibold mb-2" v-html="questions[qIndex].label"></div>
                                    <div class="flex flex-wrap">
                                        <span v-text="questions[qIndex].answer" class="text-sm text-grey-darker"></span>
                                    </div>
                                </div>
                                <div class="mb-4" v-for="qIndex in [5,6]" v-if="questions[4].answer == $t('SYMPTOMS_ANSWER_5_1')">
                                    <div class="text-blue text-sm font-semibold mb-2" v-html="questions[qIndex].label"></div>
                                    <div class="flex flex-wrap">
                                        <span v-text="questions[qIndex].answer" class="text-sm text-grey-darker"></span>
                                    </div>
                                </div>
                            </div>
                            <div v-else>
                                <div class="mb-4" v-for="(question, qIndex) in questions">
                                    <div class="text-blue text-sm font-semibold mb-2" v-html="questions[qIndex].label"></div>
                                    <div class="flex flex-wrap">
                                        <span v-text="questions[qIndex].answer" class="text-sm text-grey-darker"></span>
                                    </div>
                                </div>
                            </div>
                            <div class="relative">
                                <bubble>{{ $t('The_practitioner_will_be_informed_immediately_of_your_reservation') }}</bubble>
                                <button @click="confirmReservation" class="bg-blue hover:bg-blue-dark rounded text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3 mt-6">{{ $t('Confirm') }}</button>
                            </div>
                        </div>
                    </div>
                    <div v-if="step == 'submitting'">
                        <div class="flex items-center justify-center h-64">
                            <div class="text-center">
                                <span class="icon text-5xl text-grey"><i class="mdi mdi-loading mdi-spin"></i></span><br />
                                <span class="text-sm text-grey-darker mt-4">{{ $t('Loading') }}</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="step == 'submitted'">
                        <router-link :to="{ path: '/' }" class="text-green-lighter hover:text-green-lightest text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-close"></i></span>
                        </router-link>
                        <div class="flex items-center justify-center bg-green text-white rounded px-4 py-10 w-full">
                            <span class="icon text-5xl text-white"><i class="mdi mdi-verified"></i></span>
                            <span class="text-sm text-xl ml-4">{{ $t('Appointment_confirmed') }}</span>
                        </div>
                    </div>
                    <div v-if="step == 'submissionError'">
                        <router-link :to="{ path: '/' }" class="text-red-lighter hover:text-red-lightest text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-close"></i></span>
                        </router-link>
                        <div class="flex items-center justify-center bg-red text-white rounded px-4 py-10 w-full">
                            <span class="icon text-5xl text-white"><i class="mdi mdi-alert-circle"></i></span>
                            <span class="text-sm text-xl ml-4" v-text="errorText ? errorText : $t('an_error_occured')"></span>
                        </div>
                    </div>
                </div>
                <div class="mt-3 text-sm text-center text-grey-dark" v-if="step == 'submitted'">{{ $t('Redirecting') }}</div>
                <dfp-slot
                    v-if="timeslot"
                    path="/21865209613/Patients/TimeslotShow"
                    :size="[300,250]"
                    :id="`div-gpt-ad-timeslot`"
                    :medical-practice="timeslot.medicalPractice"
                    class="mt-6" />
            </div>
        </section>
    </div>
</template>

<script>
import firebase from 'firebase/app'
import helpers from '@/mixins/helpers'
import PhoneNumber from 'awesome-phonenumber'

import Bubble from '@/components/Patient/Bubble'
import HeaderNav from '@/components/Patient/HeaderNav'
import OfficeSettings from '@/components/Patient/OfficeSettings'

import officeRepository from '@/data/OfficeRepository'
import timeSlotRepository from '@/data/TimeSlotRepository'

export default {
    name: 'TimeslotShow',

    mixins: [helpers],

    components: {Bubble, HeaderNav, OfficeSettings},

    data () {
        return {
            step: 'timeslotDetails',
            isBooked: false,
            timeslot: null,
            questions: [],
            errorText: false,
            patientForm: {
                lastName: '',
                firstName: '',
                email: '',
                id: '',
                phone: '',
                age: ''
            }
        }
    },

    mounted () {
        this.prefillFromFirebase()

        this.fetchTimeslot()
    },

    methods: {
        showLogin () {
            this.$refs.headerNav.showLogin()
        },

        changeStep (step) {
            if (step != 'submissionError') {
                this.errorText = false
            }

            if (step == 'patientSymptoms') {
                // Make sure this gets loaded after the translation is done
                this.populateSymptoms()
            }

            this.step = step
            window.scrollTo(0, 0)
        },

        submitPatientForm () {
            this.$validator.validateAll().then((result) => {
                if (result) {
                    this.changeStep('patientSymptoms')
                }
            })
        },

        submitPatientSymptoms () {
            this.$validator.validateAll().then((result) => {
                if (result) {
                    this.changeStep('submissionSummary')
                }
            })
        },

        confirmReservation () {
            this.changeStep('submitting')

            let patient = {
                id: this.patientForm.id,
                firstName: this.hashPatientInfo(this.patientForm.firstName),
                lastName: this.hashPatientInfo(this.patientForm.lastName),
                age: parseInt(this.patientForm.age),
                email: this.hashPatientInfo(this.patientForm.email),
                phone: this.hashPatientInfo(this.patientForm.phone),
                symptoms: []
            }

            for (let i in this.questions) {
                if (i < 4 || this.questions[4].answer == this.$t('SYMPTOMS_ANSWER_5_1')) {
                    if (this.questions[i].answer) {
                        patient.symptoms.push({
                            question: this.questions[i].label.replace('&nbsp;', ' '),
                            answer: this.questions[i].answer
                        })
                    }
                }
            }

            const timeslotRemapped = {...this.timeslot, symptoms: [...this.questions]}

            timeSlotRepository.bookForPatient(patient, timeslotRemapped, () => {
                this.isBooked = true
                this.changeStep('submitted')

                this.$ga.event({
                    eventCategory: 'TimeSlots',
                    eventAction: 'BookTimeSlot'
                })

                setTimeout(() => {
                    this.$router.push('/')
                }, 3000)
            }, (error) => {
                window.bugsnagClient && window.bugsnagClient.notify(error.message)
                this.errorText = error.message
                this.changeStep('submissionError')
            })
        },

        prefillFromFirebase () {
            let firebaseUser = firebase.auth().currentUser

            if (firebaseUser) {
                this.patientForm.id = firebaseUser.uid

                this.patientForm.email = firebaseUser.email
                this.patientForm.phone = firebaseUser.phoneNumber

                let splittedName = firebaseUser.displayName.split(" ")
                this.patientForm.firstName = splittedName[0]
                delete splittedName[0]
                this.patientForm.lastName = (splittedName.join(" ")).trim()
            }
        },

        populateSymptoms () {
            if (this.timeslot.type === 'DENTIST') {
                this.questions = [
                    // 1
                    {
                        label: this.$t('SYMPTOMS_QUESTION_1'),
                        choices: [
                            this.$t('SYMPTOMS_ANSWER_1_1'),
                            this.$t('SYMPTOMS_ANSWER_1_2'),
                            this.$t('SYMPTOMS_ANSWER_1_3'),
                            this.$t('SYMPTOMS_ANSWER_1_4'),
                        ],
                        answer: null
                    },
                    // 2
                    {
                        label: this.$t('SYMPTOMS_QUESTION_2'),
                        choices: [
                            this.$t('SYMPTOMS_ANSWER_2_1'),
                            this.$t('SYMPTOMS_ANSWER_2_2'),
                            this.$t('SYMPTOMS_ANSWER_2_3'),
                            this.$t('SYMPTOMS_ANSWER_2_4'),
                        ],
                        answer: null
                    },
                    // 3
                    {
                        label: this.$t('SYMPTOMS_QUESTION_3'),
                        choices: [
                            this.$t('SYMPTOMS_ANSWER_3_1'),
                            this.$t('SYMPTOMS_ANSWER_3_2'),
                        ],
                        answer: null
                    },
                    // 4
                    {
                        label: this.$t('SYMPTOMS_QUESTION_4'),
                        choices: [
                            this.$t('SYMPTOMS_ANSWER_4_1'),
                            this.$t('SYMPTOMS_ANSWER_4_2'),
                        ],
                        answer: null
                    },
                    // 5
                    {
                        label: this.$t('SYMPTOMS_QUESTION_5'),
                        choices: [
                            this.$t('SYMPTOMS_ANSWER_5_1'),
                            this.$t('SYMPTOMS_ANSWER_5_2'),
                        ],
                        answer: null
                    },
                    // 6
                    {
                        label: this.$t('SYMPTOMS_QUESTION_6'),
                        choices: [
                            this.$t('SYMPTOMS_ANSWER_6_1'),
                            this.$t('SYMPTOMS_ANSWER_6_2'),
                            this.$t('SYMPTOMS_ANSWER_6_3'),
                            this.$t('SYMPTOMS_ANSWER_6_4'),
                        ],
                        answer: null
                    },
                    // 7
                    {
                        label: this.$t('SYMPTOMS_QUESTION_7'),
                        choices: [
                            this.$t('SYMPTOMS_ANSWER_7_1'),
                            this.$t('SYMPTOMS_ANSWER_7_2'),
                        ],
                        answer: null
                    }
                ]
            } else {
                this.questions = [
                    {
                        label: this.$t('SYMPTOMS_QUESTION'),
                        answer: ''
                    },
                ]
            }
        },

        fetchTimeslot () {
            this.timeslotId = this.$route.params.id

            timeSlotRepository.find(this.timeslotId, async (timeslot) => {
                // Load office
                officeRepository.find(timeslot.officeId, (office) => {

                    if (!office || !office.isDoctorCertified) {
                        this.alertPatientError(this.$t('timeslots_could_not_be_loaded'))
                        return this.$router.push('/')
                    } else {
                        this.timeslot = {...timeslot, isDoctorMemberOfOsteopathie: office.isDoctorMemberOfOsteopathie, officeSettings: office.officeSettings}
                        this.isBooked = timeslot.appointmentId ? true : false
                    }
                }, (error) => {
                    this.alertPatientError(this.$t('timeslots_could_not_be_loaded'))
                    return this.$router.push('/')
                })
            }, () => {
                console.log('TIMESLOT ERROR')
                this.alertPatientError(this.$t('timeslots_could_not_be_loaded'))
                return this.$router.push('/')
            })
        },

        transformPhone () {
            if (this.patientForm.phone) {
                let phoneNumber = new PhoneNumber(this.patientForm.phone, "BE")

                if (phoneNumber.isValid()) {
                    this.patientForm.phone = phoneNumber.getNumber( 'international' );
                }
            }
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/Patient/TimeslotShow.vue